import * as platform from '../../shared/helpers/platformApi';

import { getCountryCode, isMac, isWindows } from '../../shared/helpers/util';
import type { Store } from '../store/types';

const getCallsActions = () => import('../reducers/calls');
const getVcrmActions = () => import('../reducers/vcrm');

let store: Store;

const CONNECTION_TIMEOUT = 30000;

const rootDomain = 'localhost:10443';
const baseUrl = `https://${rootDomain}/api/v1`;
const applicationId = '1d0cbebe-7806-4898-a53c-4a3a73434139';
const wsUrl = `wss://${rootDomain}/api/v1/events/${applicationId}`;

let webSocket: WebSocket;

interface ApiSearchContactResponseItem {
  AddinId: string;
  AddinName: string;
  Records: [
    {
      UniqueId: string;
      RecordType: string;
      DisplayText: string;
      Fields: {
        Name: string;
        Value: string; // phones are in e164
        WellKnownName: string;
        DescriptorId: string;
        Type: string;
        Ordinal: number;
      }[];
      Actions: {
        UniqueId: string;
        ActionId: string;
        IsMultiple: boolean;
        UseDefaultImage: boolean;
        DisplayText: string;
        SpecialKind: string;
        Icon?: {
          MimeType: string;
          Buffer64: string;
        };
        Url?: string;
      }[];
    },
  ];
}

export interface SearchContactResultItem {
  integrationName: string;
  records: {
    displayText: string;
    name: string;
    email: string;
    phones: {
      name: string;
      value: string;
    }[];
    icon?: {
      mimeType: string;
      base64Url: string;
    };
    showRecordActionId?: string;
    createPhoneActivityActionId?: string;
  }[];
}

export const initPartnerCrmHelper = (s: Store) => {
  store = s;
};

export const dialNumber = async (number: string) => {
  const { startCall } = await getCallsActions();
  store.dispatch(startCall({ number }));
  platform.focusWindow();
};

export const initWs = () => {
  webSocket = new WebSocket(wsUrl);
  webSocket.onerror = (e) => {
    console.error('SOCKET ERROR', e);
  };
  webSocket.onclose = (e) => {
    console.warn('SOCKET CLOSE', e);
    // setTimeout(() => {
    //   console.log('TIMEOUT', new Date().toUTCString());
    //   initWs();
    // }, 2000);
  };
  webSocket.onmessage = (e) => {
    let data;
    try {
      data = JSON.parse(e.data);
      if (!data || !data.EventType) {
        return;
      }
    } catch (err) {
      console.error(err);
      return;
    }
    switch (data.EventType) {
      case 'MakeCallRequest': {
        const number = data.Payload.Tel;
        dialNumber(number);
        break;
      }
      default: {
        return;
      }
    }
  };
};

export const ping = async () => {
  try {
    await fetch(`${baseUrl}/Version`, {
      mode: 'no-cors',
    });
    return true;
  } catch (_) {
    return false;
  }
};

let partnerCrmPingInterval: ReturnType<typeof setInterval>;

export const clearPartnerCrmPingInterval = () => {
  if (partnerCrmPingInterval) {
    clearInterval(partnerCrmPingInterval);
  }
};

export const launch = async (vcrmLaunchToken: string, country?: string) => {
  // await unload();
  const startedTime = Date.now();
  const { registeredStateChanged } = await getVcrmActions();

  store.dispatch(registeredStateChanged({ isRegistering: true }));

  if (isMac()) {
    window.location.href = `integratorlaunch:${vcrmLaunchToken},${getCountryCode(country)},${process.env.PCRM_CODE}`;
  } else if (isWindows()) {
    window.location.href = `IntegratorLaunch:RUN,INTEGRATOR,user=${vcrmLaunchToken};tel=${getCountryCode(country)};accesscode=${process.env.PCRM_CODE}`;
  }

  partnerCrmPingInterval = setInterval(async () => {
    const res = await pingAndRegister();
    if (res) {
      const vcrmActions = await getVcrmActions();
      store.dispatch(
        vcrmActions.registeredStateChanged({ isRegistering: false }),
      );
      initWs();
    } else if (Date.now() - startedTime >= CONNECTION_TIMEOUT) {
      clearPartnerCrmPingInterval();
      const vcrmActions = await getVcrmActions();
      store.dispatch(
        vcrmActions.registeredStateChanged({ isRegistering: false }),
      );
    }
  }, 3000);
};

export const register = async () => {
  clearPartnerCrmPingInterval();
  const body = new URLSearchParams();
  body.append('Id', applicationId);
  body.append('Name', 'Communicator');
  const { registeredStateChanged } = await getVcrmActions();
  return fetch(`${baseUrl}/Register`, {
    body,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    method: 'POST',
  }).then((res) => {
    store.dispatch(registeredStateChanged({ isRegistered: true }));
    return res;
  });
};

export const pingAndRegister = async () => {
  const pingRes = await ping();
  if (!pingRes) {
    return false;
  }
  return await register();
};

export const getVersion = async () => {
  const res = await fetch(`${baseUrl}/Version`, {
    headers: {
      ApplicationId: applicationId,
    },
  });
  return res;
};

export const showWindow = () => {
  const body = new URLSearchParams();
  body.append('window', 'configuration');
  return fetch(`${baseUrl}/ShowWindow`, {
    headers: {
      ApplicationId: applicationId,
    },
    method: 'POST',
    body,
  });
};

export const searchContact = async (phoneNumber: string) => {
  const body = new URLSearchParams();
  body.append('query', phoneNumber);
  body.append('includePictures', 'true');
  body.append('includeActivities', 'true');
  // body.append('count', 5);
  // body.append('offset', 0);
  const response = await fetch(`${baseUrl}/Search`, {
    headers: {
      ApplicationId: applicationId,
    },
    method: 'POST',
    body,
  });
  const res: ApiSearchContactResponseItem[] = await response.json();
  const results: SearchContactResultItem[] = [];

  res
    .filter((r) => r.Records && r.Records.length > 0)
    .forEach((r) => {
      const records = r.Records.map((record) => {
        const safeGetFieldValue = (fieldName: string) => {
          const field = record.Fields.find((f) => f.Name === fieldName);
          if (!field) {
            return '';
          }
          return field.Value || '';
        };

        let icon;
        let showRecordActionId;
        let createPhoneActivityActionId;

        if (record.Actions) {
          const showRecordAction = record.Actions.find(
            (a) => a.ActionId === 'ShowRecord',
          );
          const createPhoneActivityAction = record.Actions.find(
            (a) => a.ActionId === 'CreatePhoneActivity',
          );

          showRecordActionId = showRecordAction?.UniqueId;
          if (showRecordAction?.Icon) {
            icon = {
              mimeType: showRecordAction.Icon.MimeType,
              base64Url: showRecordAction.Icon.Buffer64,
            };
          }
          createPhoneActivityActionId = createPhoneActivityAction?.UniqueId;
        }

        const phones = record.Fields.filter(
          (f) => f.WellKnownName === 'Phone' && !!f.Value,
        ).map((f) => ({
          name: f.Name,
          value: f.Value,
        }));

        return {
          displayText: record.DisplayText,
          name: safeGetFieldValue('Name'),
          email: safeGetFieldValue('Email'),
          phones,
          icon,
          showRecordActionId,
          createPhoneActivityActionId,
        };
      });
      results.push({ integrationName: r.AddinName, records });
    });

  return results;
};

export const doAction = async (
  actionId: any,
  bodyObj?: Record<string, any>,
) => {
  const body = new URLSearchParams();
  body.append('actionId', actionId);
  if (bodyObj) {
    Object.keys(bodyObj).forEach((key) => {
      body.append(key, bodyObj[key]);
    });
  }
  return await fetch(`${baseUrl}/DoAction`, {
    headers: {
      ApplicationId: applicationId,
    },
    method: 'POST',
    body,
  });
};

export const unload = async () => {
  const { registeredStateChanged } = await getVcrmActions();
  return await fetch(`${baseUrl}/Unload`, { method: 'POST' })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error(err);
    })
    .finally(() => {
      clearPartnerCrmPingInterval();
      store.dispatch(
        registeredStateChanged({ isRegistered: false, isRegistering: false }),
      );
    });
};

window.addEventListener('beforeunload', () => {
  if (store.getState().vcrm.isRegistered) {
    unload();
  }
  return null;
});
